import { AuthenticationError, BusinessValidationError, NetworkError, RepositoryError, ValidationError, } from '@repo/domain-service';
import { GraphQlErrorMessage } from '../constants/graphQlErrorMessages';
import { BUSINESS_VALIDATION_CODE_MAP } from './businessValidationCodeMap';
const businessValidationCodeLookup = BUSINESS_VALIDATION_CODE_MAP;
const BUSINESS_RULE_VIOLATION_CATEGORY = 'BUSINESS_RULE_VIOLATION';
/**
 * Translate a urql `CombinedError` into a typed `RepositoryError` subclass.
 *
 * - `error.networkError` → `NetworkError`
 * - first `graphQLError` with `message === "Unauthenticated."` → `AuthenticationError`
 *   (excluded from Sentry; the global auth observer redirects to login)
 * - first `graphQLError` with `extensions.validation` → `ValidationError` (per-field map)
 * - first `graphQLError` with `extensions.category === "BUSINESS_RULE_VIOLATION"` and a
 *   recognised `extensions.code` (see `BUSINESS_VALIDATION_CODE_MAP`) → `BusinessValidationError`
 *   carrying the camelCase frontend code, optional `field`, and `context`. The backend's
 *   human message is intentionally discarded — the consuming component owns translation.
 *   Excluded from Sentry as an expected user-facing failure.
 * - any other `graphQLError` (including `BUSINESS_RULE_VIOLATION` with an unmapped or
 *   missing `code`) → plain `RepositoryError` carrying its message (reported to Sentry —
 *   the unmapped-code path is intentional: it surfaces backend contract additions so we
 *   can add a translation before release).
 * - no recognisable cause → `RepositoryError(fallbackMessage)`
 *
 * The `cause` field is preserved on every returned error for Sentry / debugging.
 */
export function parseGraphQlError(error, fallbackMessage) {
    if (error.networkError) {
        return new NetworkError(error.networkError.message, { cause: error });
    }
    const graphQlError = error.graphQLErrors[0];
    if (graphQlError) {
        if (graphQlError.message === GraphQlErrorMessage.Unauthenticated) {
            return new AuthenticationError(graphQlError.message, { cause: error });
        }
        const extensions = graphQlError.extensions;
        const validation = extensions?.validation;
        if (validation && Object.keys(validation).length > 0) {
            return new ValidationError(graphQlError.message, validation, { cause: error });
        }
        if (extensions?.category === BUSINESS_RULE_VIOLATION_CATEGORY) {
            const businessCode = extensions.code ? businessValidationCodeLookup[extensions.code] : undefined;
            if (businessCode) {
                return new BusinessValidationError(businessCode, {
                    field: extensions.field ?? null,
                    context: extensions.context ?? null,
                    cause: error,
                });
            }
        }
        return new RepositoryError(graphQlError.message, { cause: error });
    }
    return new RepositoryError(fallbackMessage, { cause: error });
}
